import { motion } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/SEOHead';

export default function DienstenPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://dsfcartech.be';

  const serviceCategories = [
    {
      id: 'smartphone',
      title: 'Smartphone & Multimedia',
      description: 'Moderne connectiviteit voor uw auto met CarPlay, Android Auto en Android schermen',
      icon: 'ri-smartphone-line',
      color: 'from-cyan-400 to-blue-500',
      image: 'https://static.readdy.ai/image/9e62058216dff63d1a686833c97014ce/95842c940ae17c4b913fe5fce8e14e37.jpeg',
      services: ['CarPlay & Android Auto', 'Android Schermen'],
      link: '/diensten/smartphone-multimedia',
    },
    {
      id: 'interieur',
      title: 'Interieur Upgrades',
      description: 'Luxe en sfeer voor uw auto-interieur met sterrenhemel, verlichting en camera\'s',
      icon: 'ri-star-line',
      color: 'from-purple-400 to-pink-500',
      image: 'https://static.readdy.ai/image/9e62058216dff63d1a686833c97014ce/b97434262329e812a17292bb4b7d2069.jpeg',
      services: ['Sterrenhemel', 'Sfeerverlichting', 'Achteruitrijcamera'],
      link: '/diensten/sterrenhemel-sfeerverlichting-achteruitcamera-in-antwerpen',
    },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Auto Elektronica Upgrades - DSF Cartech",
    "description": "Professionele auto-upgrades: CarPlay, Android Auto, sterrenhemel, sfeerverlichting, camera's en Android schermen in Antwerpen.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "DSF Cartech",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Ternesselei 191",
        "addressLocality": "Wommelgem",
        "addressRegion": "Antwerpen",
        "postalCode": "2160",
        "addressCountry": "BE"
      }
    },
    "areaServed": "Antwerpen",
    "serviceType": ["CarPlay installatie", "Android Auto", "Sterrenhemel", "Sfeerverlichting", "Achteruitrijcamera", "Android schermen"]
  };

  return (
    <>
      <SEOHead
        title="Diensten - Auto Upgrades Antwerpen | DSF Cartech"
        description="Ontdek onze professionele auto-upgrades: CarPlay, Android Auto, sterrenhemel, RGB sfeerverlichting, achteruitrijcamera's en Android schermen. Betrouwbare installatie in Antwerpen."
        keywords="auto upgrades antwerpen, carplay installatie, android auto, sterrenhemel auto, sfeerverlichting auto, achteruitrijcamera, android scherm auto, auto elektronica antwerpen"
        canonical={`${siteUrl}/diensten`}
        schema={schema}
      />

      <div className="bg-black text-white min-h-screen">
        <Navigation />

        {/* Hero Section */}
        <section className="relative pt-24 md:pt-32 pb-12 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-950/20 via-black to-black"></div>
          
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/30 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-block mb-4 md:mb-6"
              >
                <div className="px-4 md:px-6 py-2 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border-2 border-cyan-400/50 rounded-full backdrop-blur-sm">
                  <span className="text-cyan-300 text-xs md:text-sm font-bold tracking-widest uppercase">Onze Diensten</span>
                </div>
              </motion.div>

              <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight px-4" style={{ fontFamily: 'Orbitron, monospace' }}>
                <span className="text-white">Professionele Auto</span>
                <br />
                <span className="bg-gradient-to-r from-cyan-300 via-purple-300 to-pink-300 bg-clip-text text-transparent">
                  Upgrades
                </span>
              </h1>

              <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed px-4">
                Transformeer uw auto met onze professionele elektronica upgrades. Van CarPlay tot sterrenhemel, wij maken uw rijervaring uniek.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Service Categories */}
        <section className="py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12">
              {serviceCategories.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="relative group"
                >
                  <div className={`absolute -inset-4 bg-gradient-to-br ${category.color} opacity-20 blur-2xl group-hover:opacity-30 transition-opacity duration-500 rounded-3xl`}></div>
                  
                  <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden hover:bg-white/10 transition-all duration-300">
                    {/* Image */}
                    <div className="relative h-64 md:h-80 overflow-hidden">
                      <img
                        src={category.image}
                        alt={category.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                    </div>

                    {/* Content */}
                    <div className="p-6 md:p-8">
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-gradient-to-br ${category.color} rounded-2xl shadow-2xl`}>
                          <i className={`${category.icon} text-2xl md:text-3xl text-white`}></i>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Orbitron, monospace' }}>
                          {category.title}
                        </h2>
                      </div>

                      <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-6">
                        {category.description}
                      </p>

                      {/* Services List */}
                      <div className="space-y-3 mb-6">
                        {category.services.map((service, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <i className={`ri-check-line text-lg bg-gradient-to-r ${category.color} bg-clip-text text-transparent`}></i>
                            <span className="text-gray-300 text-sm md:text-base">{service}</span>
                          </div>
                        ))}
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => window.REACT_APP_NAVIGATE(category.link)}
                        className={`w-full px-6 py-3 md:py-4 bg-gradient-to-r ${category.color} text-white font-bold rounded-xl whitespace-nowrap cursor-pointer shadow-2xl hover:shadow-3xl transition-all duration-300`}
                      >
                        <span className="flex items-center justify-center gap-2">
                          <span className="text-sm md:text-base">Bekijk Diensten</span>
                          <i className="ri-arrow-right-line text-lg md:text-xl"></i>
                        </span>
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-cyan-950/20 to-black"></div>
          
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/40 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 text-white" style={{ fontFamily: 'Orbitron, monospace' }}>
                Klaar voor een <span className="bg-gradient-to-r from-cyan-300 to-purple-300 bg-clip-text text-transparent">Upgrade?</span>
              </h2>
              <p className="text-base md:text-xl text-gray-300 mb-8 md:mb-12 leading-relaxed px-4">
                Neem contact op voor een vrijblijvend adviesgesprek en ontdek wat wij voor uw auto kunnen betekenen.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center px-4">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => window.REACT_APP_NAVIGATE('/contact')}
                  className="w-full sm:w-auto px-8 md:px-12 py-4 md:py-5 bg-gradient-to-r from-cyan-500 to-blue-500 text-black text-base md:text-lg font-bold rounded-2xl whitespace-nowrap cursor-pointer shadow-2xl shadow-cyan-500/50 hover:shadow-cyan-500/70 transition-all duration-300"
                >
                  <span className="flex items-center justify-center gap-3">
                    <i className="ri-calendar-check-line text-xl md:text-2xl"></i>
                    <span>Maak een Afspraak</span>
                  </span>
                </motion.button>

                <motion.a
                  href="tel:+32489170592"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full sm:w-auto px-8 md:px-12 py-4 md:py-5 bg-white/10 backdrop-blur-xl border-2 border-white/30 text-white text-base md:text-lg font-bold rounded-2xl whitespace-nowrap cursor-pointer hover:bg-white/20 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <span className="flex items-center justify-center gap-3">
                    <i className="ri-phone-line text-xl md:text-2xl"></i>
                    <span>Bel Direct</span>
                  </span>
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
